import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileSignature, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  Pen,
  Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api/hr';

// Signature font styles
const SIGNATURE_FONTS = [
  { name: 'Elegant', style: 'font-family: "Dancing Script", cursive; font-size: 32px;' },
  { name: 'Classic', style: 'font-family: "Great Vibes", cursive; font-size: 36px;' },
  { name: 'Modern', style: 'font-family: "Pacifico", cursive; font-size: 28px;' },
  { name: 'Simple', style: 'font-family: "Caveat", cursive; font-size: 32px;' }
];

export default function SignDocument() {
  const { documentId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState(null);
  const [document, setDocument] = useState(null);
  const [signer, setSigner] = useState(null);
  
  const [signatureName, setSignatureName] = useState('');
  const [selectedFont, setSelectedFont] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    fetchDocument();
  }, [documentId, token]);

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`${API}/sign/${documentId}/${token}`);
      setDocument(response.data.document);
      setSigner(response.data.signer);
      setSignatureName(response.data.signer.name);
    } catch (error) {
      console.error('Error fetching document:', error);
      setError(error.response?.data?.detail || 'Unable to load document');
    } finally {
      setLoading(false);
    }
  };

  const generateSignatureImage = () => {
    // Create a canvas to generate signature image
    const canvas = window.document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw signature
    ctx.fillStyle = '#0f172a';
    ctx.font = SIGNATURE_FONTS[selectedFont].style.includes('Dancing') ? '36px Dancing Script' :
               SIGNATURE_FONTS[selectedFont].style.includes('Great Vibes') ? '40px Great Vibes' :
               SIGNATURE_FONTS[selectedFont].style.includes('Pacifico') ? '32px Pacifico' :
               '36px Caveat';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(signatureName, canvas.width / 2, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  };

  const handleSign = async () => {
    if (!agreedToTerms) {
      toast.error('Please agree to the terms to sign');
      return;
    }
    
    if (!signatureName.trim()) {
      toast.error('Please enter your name for the signature');
      return;
    }

    setSigning(true);
    try {
      const signatureImage = generateSignatureImage();
      
      await axios.post(`${API}/sign/${documentId}/${token}`, {
        signature: signatureImage,
        signed_name: signatureName
      });
      
      setSigned(true);
      toast.success('Document signed successfully!');
    } catch (error) {
      console.error('Error signing document:', error);
      toast.error(error.response?.data?.detail || 'Failed to sign document');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-lg">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Unable to Load Document</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-lg">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Document Signed!</h1>
          <p className="text-slate-600 mb-6">
            Thank you for signing. All parties will receive a copy once the document is fully executed.
          </p>
          <p className="text-sm text-slate-500">You can close this window now.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Google Fonts for signatures */}
      <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Pacifico&family=Caveat:wght@600&display=swap" rel="stylesheet" />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
              <FileSignature className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">MyHR Orbit</h1>
              <p className="text-xs text-slate-500">Digital Signature</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-600">Signing as:</p>
            <p className="font-medium text-slate-900">{signer?.name}</p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto py-8 px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Document Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">{document?.title}</h2>
                <p className="text-sm text-slate-500">{document?.document_type?.replace('_', ' ').toUpperCase()}</p>
              </div>
              <div 
                className="p-6 prose max-w-none"
                dangerouslySetInnerHTML={{ __html: document?.content }}
              />
            </div>
          </div>

          {/* Signing Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sticky top-24">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Pen className="h-5 w-5 text-emerald-500" />
                Sign Document
              </h3>

              {/* Signature Input */}
              <div className="mb-4">
                <Label>Type your full name</Label>
                <Input
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="Your full name"
                  className="mt-1"
                />
              </div>

              {/* Signature Preview */}
              <div className="mb-4">
                <Label>Your Signature</Label>
                <div className="mt-2 p-4 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 min-h-[80px] flex items-center justify-center">
                  {signatureName ? (
                    <span style={{
                      fontFamily: SIGNATURE_FONTS[selectedFont].style.includes('Dancing') ? '"Dancing Script", cursive' :
                                  SIGNATURE_FONTS[selectedFont].style.includes('Great Vibes') ? '"Great Vibes", cursive' :
                                  SIGNATURE_FONTS[selectedFont].style.includes('Pacifico') ? '"Pacifico", cursive' :
                                  '"Caveat", cursive',
                      fontSize: '32px',
                      color: '#0f172a'
                    }}>
                      {signatureName}
                    </span>
                  ) : (
                    <span className="text-slate-400">Your signature will appear here</span>
                  )}
                </div>
              </div>

              {/* Font Selection */}
              <div className="mb-6">
                <Label>Signature Style</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {SIGNATURE_FONTS.map((font, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedFont(idx)}
                      className={`p-2 rounded-lg border text-sm transition-all ${
                        selectedFont === idx 
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {font.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Agreement */}
              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-slate-600">
                    I agree that this electronic signature is the legal equivalent of my manual signature on this document.
                  </span>
                </label>
              </div>

              {/* Sign Button */}
              <Button
                onClick={handleSign}
                disabled={signing || !agreedToTerms || !signatureName.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              >
                {signing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {signing ? 'Signing...' : 'Sign Document'}
              </Button>

              <p className="text-xs text-slate-500 mt-4 text-center">
                By signing, you agree to the terms of this document
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
